<template>
  <div class="access-control-record-tab">
    <BobProPage
      ref="bobProPageRef"
      :columns="tableColumns"
      :tableConfig="tableConfig"
      :pageConfig="pageConfig"
    />

    <ViewQRCodeDialog
      v-if="modal.viewQRCode"
      :data="selectRow"
      @close="modal.viewQRCode = false"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, reactive } from 'vue'
import { useShop } from '@/use/shop'
import BobProPage from '@/components/BobProPage.vue'
import ViewQRCodeDialog from '@/views/AccessControl/components/ViewQRCodeDialog.vue'
import {
  GetMiezoAccessControlPasswordList,
  GetMiezoAccessControlPasswordCount,
  RegenerateMiezoAccessControlPassword,
} from '@/api/miezoAccessControlPassword'
import { effectiveStatusConfig } from '@/config/miezoAccessControl'
import { formatDateRange } from '@/utils/date'
import { i18n } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'AccessControlRecordTab',
  components: {
    BobProPage,
    ViewQRCodeDialog,
  },
  props: {
    member: {
      type: Object,
      required: true,
    },
  },
  setup (props) {
    const { shopId } = useShop()
    const bobProPageRef = ref(null)

    const modal = reactive({
      viewQRCode: false,
    })

    const selectRow = ref(null)

    /**
     * 取得適用門禁名稱
     * @param {Object} row - 列資料
     * @returns {string} 門禁名稱列表
     */
    const getAccessControlNames = (row) => {
      const names = (row.MiezoAccessControls || []).map((ac) => ac.name)
      return names.length > 0 ? names.join('、') : '-'
    }

    /**
     * 重建密碼
     * @param {Object} row - 列資料
     */
    const handleRegenerate = async (row) => {
      const [, err] = await RegenerateMiezoAccessControlPassword({
        shopId: shopId.value,
        id: row.id,
      })
      if (err) {
        window.$message.error(err)
        return
      }
      window.$message.success(i18n.t('member.tab.accessControlRecord.passwordRegenerated'))
      bobProPageRef.value?.refresh()
    }

    /**
     * 檢視 QR Code
     * @param {Object} row - 列資料
     */
    const handleViewQRCode = (row) => {
      selectRow.value = row
      modal.viewQRCode = true
    }

    // 表格欄位定義
    const tableColumns = computed(() => [
      {
        prop: 'password',
        label: i18n.t('member.tab.accessControlRecord.password'),
        width: 150,
        component: () => import('@/views/AccessControl/components/PasswordColumn.vue'),
        props: (row) => ({
          row,
        }),
        on: (row) => ({
          viewQRCode: () => handleViewQRCode(row),
          regenerate: () => handleRegenerate(row),
        }),
      },
      {
        prop: 'accessControls',
        label: i18n.t('member.tab.accessControlRecord.applicableAccessControl'),
        component: 'div',
        render: (row) => getAccessControlNames(row),
      },
      {
        prop: 'orderCode',
        label: i18n.t('member.tab.accessControlRecord.orderCode'),
        component: () => import('./components/OrderCodeColumn.vue'),
        props: (row) => ({
          row,
        }),
      },
      {
        prop: 'effectiveRange',
        label: i18n.t('member.tab.accessControlRecord.effectiveRange'),
        width: 320,
        component: 'div',
        render: (row) => formatDateRange({
          dates: [row.effectiveStartAt, row.effectiveEndAt],
        }),
      },
      {
        prop: 'effectiveStatus',
        label: i18n.t('member.tab.accessControlRecord.status'),
        component: () => import('@/components/Tag/StatusDisplay.vue'),
        props: (row) => {
          const status = row.effectiveStatus
          return {
            tagType: effectiveStatusConfig[status]?.tagType || 'info',
            isTag: true,
            displayText: effectiveStatusConfig[status]?.label || '-',
          }
        },
      },
    ])

    // 表格設定
    const tableConfig = computed(() => ({
      pagination: {
        enabled: true,
        pageLimit: 10,
        pageSizes: [10, 20, 50],
      },
      apis: {
        get: GetMiezoAccessControlPasswordList,
        count: GetMiezoAccessControlPasswordCount,
        getPayload: () => ({
          shopId: shopId.value,
          memberId: props.member.id,
        }),
      },
      filters: [],
    }))

    // 頁面設定
    const pageConfig = computed(() => ({
      title: '',
      actions: [],
    }))

    return {
      bobProPageRef,
      modal,
      selectRow,
      tableColumns,
      tableConfig,
      pageConfig,
    }
  },
})
</script>

<style lang="postcss" scoped>
.access-control-record-tab {
  @apply w-full;
}
</style>
